/**
 * The mascot. Four states, nothing more (docs/UX.md §9).
 * Inline SVG so it works offline and costs no extra request.
 * The fox never speaks and never carries information on its own —
 * every place it appears, text next to it says the same thing.
 */
export type FoxMood = "neutral" | "happy" | "think" | "sleep";

const ORANGE = "#E85D04";
const DEEP = "#C2410C";
const INK = "#2C1810";
const CREAM = "#FDF3E3";

// Ears sit behind the head: small and blunt-tipped, so the fox reads as friendly
// rather than sharp. The round stroke on each triangle is what softens the tips.
const EAR_L = "M12.5 11 L26 19.5 L13.5 30.5 Z";
const EAR_R = "M51.5 11 L38 19.5 L50.5 30.5 Z";
const EAR_L_IN = "M14.14 14.17 L23.05 19.78 L14.8 27.04 Z";
const EAR_R_IN = "M49.86 14.17 L40.95 19.78 L49.2 27.04 Z";

const HEAD =
  "M9 32 C9 22 19.5 16 32 16 C44.5 16 55 22 55 32 C55 43 45 52.5 32 56.5 C19 52.5 9 43 9 32 Z";
const BLAZE =
  "M32 56.5 C25 53 19.6 46.8 19.2 39.6 C23.2 40.2 28 38 32 34 C36 38 40.8 40.2 44.8 39.6 C44.4 46.8 39 53 32 56.5 Z";
const NOSE =
  "M32 48.2 C29.5 48.2 28.1 46.9 28.1 45.5 C28.1 44.4 29.7 43.9 32 43.9 C34.3 43.9 35.9 44.4 35.9 45.5 C35.9 46.9 34.5 48.2 32 48.2 Z";

const stroke = { fill: "none", stroke: INK, strokeWidth: 2.1, strokeLinecap: "round" as const };
const lid = { fill: "none", stroke: INK, strokeWidth: 3, strokeLinecap: "round" as const };

function Eyes({ mood }: { mood: FoxMood }) {
  if (mood === "happy")
    return (
      <>
        <path d="M19.6 35.2 q4 -5 8 0" {...lid} />
        <path d="M36.4 35.2 q4 -5 8 0" {...lid} />
      </>
    );
  if (mood === "sleep")
    return (
      <>
        <path d="M19.6 33 q4 4.4 8 0" {...lid} />
        <path d="M36.4 33 q4 4.4 8 0" {...lid} />
      </>
    );
  if (mood === "think")
    return (
      <>
        <ellipse cx="23.6" cy="33.2" rx="2.9" ry="3.3" fill={INK} />
        <circle cx="24.7" cy="31.9" r="1" fill="#fff" />
        <path d="M36.4 33.6 q4 3.4 8 0" {...lid} />
      </>
    );
  return (
    <>
      <ellipse cx="23.6" cy="33.6" rx="2.9" ry="3.3" fill={INK} />
      <circle cx="24.7" cy="32.3" r="1" fill="#fff" />
      <ellipse cx="40.4" cy="33.6" rx="2.9" ry="3.3" fill={INK} />
      <circle cx="41.5" cy="32.3" r="1" fill="#fff" />
    </>
  );
}

function Mouth({ mood }: { mood: FoxMood }) {
  if (mood === "happy")
    return (
      <>
        <path d="M32 48.1 v2.4" {...stroke} />
        <path d="M27 49 q5 4.4 10 0" {...stroke} />
      </>
    );
  if (mood === "sleep")
    return (
      <>
        <path d="M32 48.1 v1.9" {...stroke} />
        <path d="M29.2 49.8 q2.8 1.9 5.6 0" {...stroke} />
      </>
    );
  if (mood === "think")
    return (
      <>
        <path d="M32 48.1 v2" {...stroke} />
        <path d="M28.7 50.4 q3.3 -2.1 6.6 0.6" {...stroke} />
      </>
    );
  return (
    <>
      <path d="M32 48.1 v2.2" {...stroke} />
      <path d="M28 49.3 q4 3 8 0" {...stroke} />
    </>
  );
}

export function Fox({ mood = "neutral", className = "" }: { mood?: FoxMood; className?: string }) {
  return (
    <svg viewBox="0 0 64 64" className={className} aria-hidden="true" focusable="false">
      <g stroke={ORANGE} strokeWidth="2.6" strokeLinejoin="round">
        <path d={EAR_L} fill={ORANGE} />
        <path d={EAR_R} fill={ORANGE} />
      </g>
      <g stroke={DEEP} strokeWidth="1.6" strokeLinejoin="round">
        <path d={EAR_L_IN} fill={DEEP} />
        <path d={EAR_R_IN} fill={DEEP} />
      </g>
      <path d={HEAD} fill={ORANGE} />
      <path d={BLAZE} fill={CREAM} />
      <Eyes mood={mood} />
      <path d={NOSE} fill={INK} />
      <Mouth mood={mood} />
    </svg>
  );
}

/** Fox plus wordmark. Used in the sidebar and on the welcome screen. */
export function Wordmark({
  className = "",
  foxClass = "h-9 w-9",
  textClass = "text-2xl",
}: {
  className?: string;
  foxClass?: string;
  textClass?: string;
}) {
  return (
    <span className={`inline-flex items-center gap-2.5 ${className}`}>
      <Fox mood="happy" className={foxClass} />
      <span className={`font-display font-semibold tracking-tight ${textClass}`}>
        Voci<span className="text-fox">Fuchs</span>
      </span>
    </span>
  );
}
